<template>
  <v-app style="background-color: #1a2632;">
    <loading-overlay :on-off="loading" />

    <!-- 認証チェック中のローディング画面 -->
    <v-container
      v-if="checking"
      class="d-flex flex-column align-center justify-center"
      style="min-height: 100vh;"
    >
      <v-icon size="60" color="success" class="mb-6">mdi-certificate-outline</v-icon>
      <v-progress-circular indeterminate color="success" size="40" />
    </v-container>

    <!-- LP本体（認証チェック完了後に表示） -->
    <template v-if="!checking">

    <!-- Hero セクション -->
    <v-container
      class="d-flex flex-column align-center justify-center text-center py-16"
      style="min-height: 100vh; max-width: 800px;"
    >
      <v-icon size="80" color="success" class="mb-6">mdi-certificate-outline</v-icon>

      <h1 class="white--text font-weight-bold mb-4" style="font-size: 2.4rem; line-height: 1.3;">
        あなたの資格を、<br>ひとつの場所で管理しよう
      </h1>

      <p class="grey--text text--lighten-1 mb-10" style="font-size: 1.1rem; max-width: 560px;">
        取得済み・勉強中の資格をかんたん管理。<br>
        有効期限・更新期限の見える化で、うっかり失効を防ごう。<br>
        <span style="font-size: 0.95rem;">インストール不要・スマホでもすぐ使える。</span>
      </p>

      <v-row justify="center" style="width: 100%; max-width: 520px;">
        <v-col cols="12" sm="6">
          <v-btn block large color="white" class="font-weight-bold text-none" @click="googleLogin()">
            <v-avatar size="24" class="mr-2">
              <v-img src="https://img.freepik.com/free-icon/search_318-265146.jpg" alt="Google" />
            </v-avatar>
            Googleでログイン
          </v-btn>
        </v-col>
        <v-col cols="12" sm="6">
          <v-btn block large color="success" depressed class="font-weight-bold text-none" @click="trialLogin()">
            おためしで使う（登録不要）
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <!-- Excelからの乗り換え訴求 -->
    <v-container style="max-width: 860px;" class="pb-16">
      <v-divider dark class="mb-12" />
      <h2 class="white--text font-weight-bold text-center mb-10" style="font-size: 1.6rem;">
        Excelでの資格管理、こんな悩みありませんか？
      </h2>
      <v-row justify="center">
        <v-col cols="12" sm="5">
          <v-card flat color="rgba(255,80,80,0.08)" class="pa-6 rounded-lg">
            <div class="font-weight-bold mb-4" style="color: #ef9a9a; font-size: 1rem;">Excelでの管理</div>
            <v-list color="transparent" dense>
              <v-list-item v-for="item in excelPains" :key="item" class="px-0">
                <v-list-item-icon class="mr-3"><v-icon color="red lighten-2" small>mdi-close-circle</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title class="grey--text text--lighten-1" style="font-size: 0.9rem; white-space: normal;">{{ item }}</v-list-item-title></v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
        <v-col cols="12" sm="2" class="d-flex align-center justify-center">
          <v-icon color="success" size="40">mdi-arrow-right-bold</v-icon>
        </v-col>
        <v-col cols="12" sm="5">
          <v-card flat color="rgba(80,255,120,0.08)" class="pa-6 rounded-lg">
            <div class="font-weight-bold mb-4" style="color: #a5d6a7; font-size: 1rem;">資格管理アプリなら</div>
            <v-list color="transparent" dense>
              <v-list-item v-for="item in appSolutions" :key="item" class="px-0">
                <v-list-item-icon class="mr-3"><v-icon color="success" small>mdi-check-circle</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title class="grey--text text--lighten-1" style="font-size: 0.9rem; white-space: normal;">{{ item }}</v-list-item-title></v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- 機能紹介 -->
    <v-container style="max-width: 860px;" class="pb-16">
      <v-divider dark class="mb-12" />
      <h2 class="white--text font-weight-bold text-center mb-10" style="font-size: 1.6rem;">
        こんなことができます
      </h2>
      <v-row>
        <v-col v-for="feature in features" :key="feature.title" cols="12" sm="6" class="mb-4">
          <v-card flat color="rgba(255,255,255,0.05)" class="pa-6 rounded-lg" height="100%">
            <v-icon :color="feature.color" size="36" class="mb-4">{{ feature.icon }}</v-icon>
            <div class="white--text font-weight-bold mb-2" style="font-size: 1rem;">{{ feature.title }}</div>
            <div class="grey--text text--lighten-1" style="font-size: 0.9rem;">{{ feature.description }}</div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- 使い方ステップ -->
    <v-container style="max-width: 860px;" class="pb-16">
      <v-divider dark class="mb-12" />
      <h2 class="white--text font-weight-bold text-center mb-10" style="font-size: 1.6rem;">
        3ステップで使い始められます
      </h2>
      <v-row justify="center" align="center">
        <v-col v-for="(step, i) in steps" :key="step.title" cols="12" sm="3" class="text-center">
          <div class="mb-4">
            <v-avatar color="success" size="56">
              <span class="white--text font-weight-bold" style="font-size: 1.4rem;">{{ i + 1 }}</span>
            </v-avatar>
          </div>
          <div class="white--text font-weight-bold mb-2">{{ step.title }}</div>
          <div class="grey--text text--lighten-1" style="font-size: 0.85rem;">{{ step.description }}</div>
          <div v-if="i < steps.length - 1" class="d-none d-sm-flex justify-end align-center" style="position: absolute; right: -12px; top: 28px;">
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- こんな人におすすめ -->
    <v-container style="max-width: 860px;" class="pb-16">
      <v-divider dark class="mb-12" />
      <h2 class="white--text font-weight-bold text-center mb-10" style="font-size: 1.6rem;">
        こんな方におすすめ
      </h2>
      <v-row justify="center">
        <v-col cols="12" sm="8">
          <v-list color="transparent">
            <v-list-item v-for="rec in recommendations" :key="rec" class="px-0 mb-2">
              <v-list-item-icon class="mr-4">
                <v-icon color="success">mdi-check-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="white--text" style="font-size: 1rem; white-space: normal;">
                  {{ rec }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </v-container>

    <!-- 数字で実績 -->
    <v-container style="max-width: 860px;" class="pb-16">
      <v-divider dark class="mb-12" />
      <v-row justify="center">
        <v-col v-for="stat in stats" :key="stat.label" cols="6" sm="3" class="text-center">
          <div class="success--text font-weight-bold mb-2" style="font-size: 2rem;">{{ stat.value }}</div>
          <div class="grey--text text--lighten-1" style="font-size: 0.9rem;">{{ stat.label }}</div>
        </v-col>
      </v-row>
    </v-container>

    <!-- FAQ -->
    <v-container style="max-width: 860px;" class="pb-16">
      <v-divider dark class="mb-12" />
      <h2 class="white--text font-weight-bold text-center mb-10" style="font-size: 1.6rem;">
        よくある質問
      </h2>
      <v-expansion-panels flat dark>
        <v-expansion-panel
          v-for="faq in faqs"
          :key="faq.q"
          style="background-color: rgba(255,255,255,0.05); margin-bottom: 8px;"
        >
          <v-expansion-panel-header class="white--text font-weight-bold">
            {{ faq.q }}
          </v-expansion-panel-header>
          <v-expansion-panel-content class="grey--text text--lighten-1">
            {{ faq.a }}
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <!-- 下部CTA -->
    <v-container style="max-width: 860px;" class="pb-16">
      <v-divider dark class="mb-12" />
      <h2 class="white--text font-weight-bold text-center mb-8" style="font-size: 1.6rem;">
        さっそく使ってみよう
      </h2>
      <v-row justify="center" style="max-width: 520px; margin: 0 auto;">
        <v-col cols="12" sm="6">
          <v-btn block large color="white" class="font-weight-bold text-none" @click="googleLogin()">
            <v-avatar size="24" class="mr-2">
              <v-img src="https://img.freepik.com/free-icon/search_318-265146.jpg" alt="Google" />
            </v-avatar>
            Googleでログイン
          </v-btn>
        </v-col>
        <v-col cols="12" sm="6">
          <v-btn block large color="success" depressed class="font-weight-bold text-none" @click="trialLogin()">
            おためしで使う（登録不要）
          </v-btn>
        </v-col>
      </v-row>

      <!-- フッター -->
      <div class="text-center mt-10">
        <v-btn color="grey" text small @click="$router.push('/privacy')">プライバシーポリシー</v-btn>
        <v-btn color="grey" text small @click="$router.push('/terms')">著作権・免責事項</v-btn>
        <v-btn color="grey" text small target="_blank" href="https://note.com/shikakukanri/n/n7ce1be0171e1">リリースノート</v-btn>
      </div>
      <div class="text-center mt-2 grey--text text--darken-1 caption pb-8">
        © 2023 資格管理アプリ
      </div>
    </v-container>

    </template>
  </v-app>
</template>

<script>
  import firebase from 'firebase'
  import commonMixin from './dashboard/mixin/common'
  import { funcGetDataFromFirebase } from '../functions/fb-modules'
  import { funcStoredUserInformation } from '../functions/utils'

  export default {
    name: 'LandingPage',
    components: {
      LoadingOverlay: () => import('./dashboard/components/LoadingOverlay'),
    },
    mixins: [commonMixin],
    created () {
      const unsubscribe = firebase.auth().onAuthStateChanged(user => {
        unsubscribe()
        if (user) {
          this.$router.push({ path: '/app' })
        } else {
          this.checking = false
        }
      })
    },
    data: () => ({
      checking: true,
      loading: false,
      excelPains: [
        '有効期限をいちいち手動で計算している',
        'スマホで確認しにくい',
        '更新期限をうっかり忘れてしまった',
        'ファイルがどこにあるかわからなくなる',
      ],
      appSolutions: [
        '有効期限まで自動でカウントダウン',
        'スマホからいつでも確認できる',
        '期限が近づいたら一目でわかる',
        'クラウド保存でいつでもどこでもアクセス',
      ],
      features: [
        {
          icon: 'mdi-badge-account-horizontal',
          color: 'primary',
          title: '資格一覧の管理',
          description: '取得済み・勉強中・気になる資格を一覧で整理。有効期限・更新期限も一目でわかります。',
        },
        {
          icon: 'mdi-timeline',
          color: 'purple lighten-2',
          title: 'タイムラインで振り返り',
          description: '資格取得の歴史を時系列で確認。過去から未来までの試験スケジュールを把握できます。',
        },
        {
          icon: 'mdi-alert-circle',
          color: 'red lighten-2',
          title: '期限の見える化',
          description: '有効期限・更新期限までの残り日数を自動計算。うっかり失効を防ぎます。',
        },
        {
          icon: 'mdi-twitter',
          color: 'blue lighten-2',
          title: 'Xでシェア',
          description: '資格取得をXに投稿して、仲間と喜びを分かち合いましょう。',
        },
      ],
      steps: [
        {
          title: 'ログイン',
          description: 'Googleアカウントで1クリック登録。メール登録不要。',
        },
        {
          title: '資格を入力',
          description: '資格名・取得日・有効期限などを入力するだけ。',
        },
        {
          title: '管理スタート',
          description: '一覧・タイムラインで資格情報をいつでも確認できます。',
        },
      ],
      recommendations: [
        '資格をエクセルやメモ帳で管理している方',
        '有効期限・更新期限をうっかり忘れてしまった経験がある方',
        '複数の資格を保有していて一元管理したい方',
        '資格取得を頑張っていて、進捗を可視化したい方',
        '転職・キャリアアップに向けて資格を整理したい方',
      ],
      stats: [
        { value: '無制限', label: '登録できる資格数' },
        { value: '自動', label: 'クラウド保存' },
        { value: '無料', label: '完全無料' },
        { value: 'アプリ感覚', label: 'スマホ対応' },
      ],
      faqs: [
        {
          q: 'スマートフォンでも使えますか？',
          a: 'はい、スマートフォンのブラウザからご利用いただけます。ホーム画面に追加すると、アプリのように起動できます。',
        },
        {
          q: 'データはどこに保存されますか？',
          a: 'Googleアカウントでログインすると、データはクラウド（Firebase）に安全に保存されます。デバイスが変わっても同じデータにアクセスできます。',
        },
        {
          q: 'ログインせずに使えますか？',
          a: '「おためしで使う」ボタンから、ログインなしでサンプルデータ入りのアプリを体験できます。ただしデータは保存されません。',
        },
        {
          q: '料金はかかりますか？',
          a: '完全無料です。登録料・月額費用などは一切かかりません。',
        },
        {
          q: '対応している資格の種類に制限はありますか？',
          a: 'ありません。国家資格・民間資格・語学・IT・業務系など、あらゆる資格を自由に登録できます。',
        },
      ],
    }),
    methods: {
      trialLogin () {
        this.loading = true
        const data = this.createSampleData()
        funcStoredUserInformation(true, '', 'トライアル', 'https://kotonohaworks.com/free-icons/wp-content/uploads/kkrn_icon_user_2.png', data.shikakuList, data.studyList, data.kininaruList, data.taskList, false)
        this.loading = false
        this.$router.push({ path: '/app' })
      },
      googleLogin () {
        var provider = new firebase.auth.GoogleAuthProvider()
        provider.setCustomParameters({ prompt: 'select_account' })
        firebase.auth()
          .signInWithPopup(provider)
          .then((result) => {
            this.loading = true
            var user = result.user
            funcGetDataFromFirebase(user.uid)
              .then((resultFb) => {
                var shikakuList = []
                var studyList = []
                var kininaruList = []
                var taskList = []
                var darkMode = false
                if (Object.keys(resultFb).length) {
                  if (Object.prototype.hasOwnProperty.call(resultFb, 'shikakuList')) shikakuList = resultFb.shikakuList
                  if (Object.prototype.hasOwnProperty.call(resultFb, 'studyList')) studyList = resultFb.studyList
                  if (Object.prototype.hasOwnProperty.call(resultFb, 'kininaruList')) kininaruList = resultFb.kininaruList
                  if (Object.prototype.hasOwnProperty.call(resultFb, 'taskList')) taskList = resultFb.taskList
                  if (Object.prototype.hasOwnProperty.call(resultFb, 'darkMode')) darkMode = resultFb.darkMode
                }
                funcStoredUserInformation(true, user.uid, 'Googleアカウント', 'https://img.freepik.com/free-icon/search_318-265146.jpg', shikakuList, studyList, kininaruList, taskList, darkMode)
                this.$router.push({ path: '/app' })
                this.loading = false
              }).catch((error) => {
                console.error('Failed to get data from firebase', error)
                this.loading = false
              })
          }).catch((error) => {
            console.error('Login failed.', error)
            this.loading = false
          })
      },
    },
  }
</script>
